import React from 'react';
import { Wrench, Settings, AlertTriangle, Shield, Clock, MapPin, CreditCard, MessageCircle } from 'lucide-react';

const services = [
  {
    id: 1,
    name: "Mecánica a domicilio",
    description: "¿Tu carro está fallando? Te ofrecemos Atención Primaria de Mecánica en Caracas, Lara y Valencia, de lunes a viernes, de 8:00 am a 5:00 pm.",
    icon: AlertTriangle,
    price: "Sin costo de afiliaciòn",
    duration: "",
    color: "bg-red-500",
    features: ["Disponible 8/5", "Respuesta confiable", "Geolocalización activa"]
  },
  {
    id: 2,
    name: "Diagnóstico ruta segura",
    description: "Agenda tu diagnóstico de 10 puntos clave a domicilio o en taller, en Caracas, Lara y Valencia, de lunes a viernes de 8:00 am a 5:00 pm.",
    icon: Settings,
    price: "Sin costo de afiliación",
    duration: "",
    color: "bg-green-500",
    features: ["Atención personalizada", "Servcios confiables", "Comidad en un clic"]
  },
  {
    id: 3,
    name: "Segunda opinión técnica",
    description: "Si no confias en el diagnóstico inicial, te brindamos una segunda opinión experta para que tomes la mejor decisión con tu vehículo.",
    icon: Wrench,
    price: "Desde tu celular",
    duration: "",
    color: "bg-blue-500",
    features: ["Servicio verificado", "Asesoría en un clic", "Calidad y confianza"]
  },
  {
    id: 4,
    name: "Atención especializada",
    description: "Si la falla amerita una atención especializada, ponemos a tu disposición talleres confiables y el respaldo de nuestra plataforma.",
    icon: Shield,
    price: "Desde tu celular",
    duration: "",
    color: "bg-purple-500",
    features: ["Proveedores validados", "Garantía de productos", "Orientación personalizada"],
    botoncaption: "Consultar"
  }
];

const features = [
  {
    icon: MapPin,
    title: "Respuesta confiable",
    description: "Recibe ayuda, justo donde estás"
  },
  {
    icon: Clock,
    title: "Servicios mecánicos verificados por reputación",
    description: "Conecta con mecánicos y talleres valorados por otros conductores, con experiencia real y confianza comprobada"
  },
  {
    icon: MessageCircle,
    title: "Atención y seguimiento vía WhatsApp",
    description: "Solicita ayuda de forma sencilla, desde tu celular, con acompañamiento humano y seguimiento en cada paso"
  },
  {
    icon: CreditCard,
    title: "Ayuda, asesoría y transparencia desde el primer clic",
    description: "Tú decides lo que necesitas, con toda la información clara antes de aceptar el servicio"
  }
];

interface ServicesProps {
  onServiceSelect: (serviceId: number) => void;
}

export const Services: React.FC<ServicesProps> = ({ onServiceSelect }) => {
  return (
    <section id="servicios" className="py-12 scroll-mt-12 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Nuestros servicios
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          En RECARX PLUS te ofrecemos en un clic servicios mecánicos a domicilio y especializados en taller, listos para atenderte con máxima comodidad para ti. Cada proveedor ha sido verificado y es valorado por conductores reales, garantizando atención profesional, precios transparentes y respuesta oportuna. Porque tu carro merece cuidado, y tú mereces tranquilidad.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {services.map((service) => {
            const IconComponent = service.icon;
            return (
              <div
                key={service.id}
                className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer group"
                onClick={() => onServiceSelect(service.id)}
              >
                <div className={`${service.color} w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <IconComponent className="h-6 w-6 text-white" />
                </div>
                
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {service.name}
                </h3>
                
                <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm leading-relaxed">
                  {service.description}
                </p>
                
                <div className="flex justify-between items-center mb-4">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {service.duration}
                  </span>
                </div>
                
                <div className="space-y-2">
                  {service.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                      <span className="text-xs text-gray-600 dark:text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <button className="w-full mt-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 rounded-lg font-medium transition-all duration-300 transform group-hover:scale-105">
                  {service.botoncaption}
                </button>
              </div>
            );
          })}
        </div>

        {/* Features */}
        <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-12">
            ¿Por qué elegir a RECARX PLUS?
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="text-center group">
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <IconComponent className="h-8 w-8 text-white" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {feature.title}
                  </h4>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};